import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { billsApi, transactionsApi, anomalyAlertsApi, netWorthApi } from '@/lib/api'
import { formatCurrency, formatDate, cn } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Receipt,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  BarChart3,
  PieChart as PieChartIcon,
  AlertTriangle,
  X
} from 'lucide-react'
import { Bill, SpendingSummary, IncomeSummary, AnomalyAlert, NetWorthCurrent } from '@/types'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from 'recharts'
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns'

// Default colors for categories without a color
const DEFAULT_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
]

type PeriodOption = 'last30' | 'thisMonth' | 'lastMonth'

function getDateRange(period: PeriodOption): { start_date: string; end_date: string; label: string } {
  const today = new Date()

  switch (period) {
    case 'last30':
      return {
        start_date: format(subDays(today, 30), 'yyyy-MM-dd'),
        end_date: format(today, 'yyyy-MM-dd'),
        label: 'Last 30 Days'
      }
    case 'thisMonth':
      return {
        start_date: format(startOfMonth(today), 'yyyy-MM-dd'),
        end_date: format(endOfMonth(today), 'yyyy-MM-dd'),
        label: format(today, 'MMMM yyyy')
      }
    case 'lastMonth':
      const lastMonth = subMonths(today, 1)
      return {
        start_date: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end_date: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        label: format(lastMonth, 'MMMM yyyy')
      }
  }
}

export default function Dashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [spendingViewMode, setSpendingViewMode] = useState<'list' | 'chart'>('list')
  const [period, setPeriod] = useState<PeriodOption>('last30')

  const dateRange = useMemo(() => getDateRange(period), [period])

  const { data: alerts = [] } = useQuery<AnomalyAlert[]>({
    queryKey: ['anomaly-alerts'],
    queryFn: async () => {
      const res = await anomalyAlertsApi.getAll()
      return res.data
    },
  })

  const dismissAlert = useMutation({
    mutationFn: (id: number) => anomalyAlertsApi.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anomaly-alerts'] })
    },
  })

  const { data: bills = [] } = useQuery<Bill[]>({
    queryKey: ['bills'],
    queryFn: async () => {
      const res = await billsApi.getAll()
      return res.data
    },
  })

  const { data: spending = [] } = useQuery<SpendingSummary[]>({
    queryKey: ['spending-summary', dateRange.start_date, dateRange.end_date],
    queryFn: async () => {
      const res = await transactionsApi.getSpending({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      })
      return res.data
    },
  })

  const { data: income = [] } = useQuery<IncomeSummary[]>({
    queryKey: ['income-summary', dateRange.start_date, dateRange.end_date],
    queryFn: async () => {
      const res = await transactionsApi.getIncome({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      })
      return res.data
    },
  })

  const { data: cashFlowData = [] } = useQuery({
    queryKey: ['cashflow'],
    queryFn: () => transactionsApi.getCashFlow(6),
  })

  const { data: netWorthData } = useQuery<NetWorthCurrent>({
    queryKey: ['net-worth-current'],
    queryFn: async () => (await netWorthApi.getCurrent()).data,
    staleTime: 5 * 60 * 1000,
  })

  const pendingBills = bills.filter(b => b.status === 'pending')
  const overdueBills = bills.filter(b => b.status === 'overdue')
  const paidBills = bills.filter(b => b.status === 'paid')

  const totalDue = pendingBills.reduce((sum, b) => sum + b.amount, 0)
  const totalOverdue = overdueBills.reduce((sum, b) => sum + b.amount, 0)
  const totalPaid = paidBills.reduce((sum, b) => sum + b.amount, 0)
  const totalSpending = spending.reduce((sum, s) => sum + s.total_amount, 0)
  const totalIncome = income.reduce((sum, s) => sum + s.total_amount, 0)

  const latestMonth = cashFlowData[cashFlowData.length - 1]
  const netCashFlow = latestMonth ? latestMonth.income - latestMonth.expense : null

  const upcomingBills = pendingBills
    .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())
    .slice(0, 5)

  // Prepare pie chart data
  const pieChartData = spending.slice(0, 8).map((cat, index) => ({
    name: cat.category_name,
    value: cat.total_amount,
    color: cat.category_color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
    category_id: cat.category_id
  }))

  // Helper to get budget status
  const getBudgetStatus = (spent: number, budget: number | null | undefined) => {
    if (!budget || budget <= 0) return { status: 'none', percentage: 0 }
    const percentage = (spent / budget) * 100
    if (percentage >= 100) return { status: 'over', percentage }
    if (percentage >= 80) return { status: 'warning', percentage }
    return { status: 'good', percentage }
  }

  // Get progress bar color based on budget status
  const getProgressColor = (status: string) => {
    switch (status) {
      case 'over': return 'bg-red-500'
      case 'warning': return 'bg-yellow-500'
      default: return ''
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Overview of your bills and spending</p>
        </div>
        <Select value={period} onValueChange={(value: PeriodOption) => setPeriod(value)}>
          <SelectTrigger className="w-[180px]">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="last30">Last 30 Days</SelectItem>
            <SelectItem value="thisMonth">This Month</SelectItem>
            <SelectItem value="lastMonth">Last Month</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/transactions?type=income')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Money In</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalIncome)}</div>
            <p className="text-xs text-muted-foreground">
              {income.length} income source{income.length !== 1 ? 's' : ''} ({dateRange.label})
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/bills?tab=pending')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due This Month</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalDue)}</div>
            <p className="text-xs text-muted-foreground">
              {pendingBills.length} pending bills
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/bills?tab=overdue')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
            <AlertCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {formatCurrency(totalOverdue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {overdueBills.length} overdue bills
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/bills?tab=paid')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid This Month</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {formatCurrency(totalPaid)}
            </div>
            <p className="text-xs text-muted-foreground">
              {paidBills.length} paid bills
            </p>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => navigate('/transactions?type=expense')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spending</CardTitle>
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalSpending)}</div>
            <p className="text-xs text-muted-foreground">
              {dateRange.label}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Net Cash Flow</CardDescription>
            <CardTitle className={`text-2xl ${netCashFlow !== null && netCashFlow >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {netCashFlow !== null ? formatCurrency(Math.abs(netCashFlow)) : '—'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {netCashFlow !== null
                ? netCashFlow >= 0 ? 'Surplus this month' : 'Deficit this month'
                : 'No data'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Worth</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {netWorthData ? formatCurrency(netWorthData.netWorth) : '—'}
            </div>
            {netWorthData?.changeFromLastMonth != null && (
              <p className={cn(
                "text-xs flex items-center gap-1 mt-1",
                netWorthData.changeFromLastMonth >= 0 ? "text-green-600" : "text-red-600"
              )}>
                {netWorthData.changeFromLastMonth >= 0
                  ? <TrendingUp className="h-3 w-3" />
                  : <TrendingDown className="h-3 w-3" />}
                {formatCurrency(Math.abs(netWorthData.changeFromLastMonth))} vs last month
              </p>
            )}
            {netWorthData && (
              <p className="text-xs text-muted-foreground mt-1">
                Assets: {formatCurrency(netWorthData.assets)} · Liabilities: {formatCurrency(netWorthData.liabilities)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Cash Flow Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Cash Flow</CardTitle>
          <CardDescription>Income vs Expenses — last 6 months</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cashFlowData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="income"  name="Income"   fill="#10B981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Anomaly Alerts */}
      {alerts.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50/50 dark:border-yellow-800 dark:bg-yellow-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <AlertTriangle className="h-5 w-5" />
              Spending Alerts ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.slice(0, 3).map((alert) => (
              <div
                key={alert.id}
                className={`flex items-start justify-between p-3 rounded-lg border ${
                  alert.severity === 'error'
                    ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30'
                    : 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950/30'
                }`}
              >
                <div className="flex-1">
                  <p className={`font-medium text-sm ${
                    alert.severity === 'error' ? 'text-red-700 dark:text-red-400' : 'text-yellow-700 dark:text-yellow-400'
                  }`}>
                    {alert.title}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {alert.message}
                  </p>
                  {alert.transaction_merchant && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {alert.transaction_merchant} on {format(new Date(alert.transaction_date!), 'MMM d')}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 h-8 w-8"
                  onClick={() => dismissAlert.mutate(alert.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {alerts.length > 3 && (
              <p className="text-sm text-muted-foreground text-center pt-2">
                +{alerts.length - 3} more alerts
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Upcoming Bills */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Upcoming Bills
            </CardTitle>
            <CardDescription>Bills due soon</CardDescription>
          </CardHeader>
          <CardContent>
            {upcomingBills.length === 0 ? (
              <p className="text-sm text-muted-foreground">No upcoming bills</p>
            ) : (
              <div className="space-y-4">
                {upcomingBills.map((bill) => (
                  <div
                    key={bill.id}
                    className="flex items-center justify-between cursor-pointer hover:bg-accent/50 rounded-lg p-2 -mx-2 transition-colors"
                    onClick={() => navigate(`/bills?tab=${bill.status}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: bill.category_color || '#6b7280' }}
                      />
                      <div>
                        <p className="text-sm font-medium">{bill.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(bill.due_date)}
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-medium">
                      {formatCurrency(bill.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Spending by Category */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Spending by Category
                </CardTitle>
                <CardDescription>{dateRange.label}</CardDescription>
              </div>
              <div className="flex gap-1">
                <Button
                  variant={spendingViewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSpendingViewMode('list')}
                >
                  <BarChart3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={spendingViewMode === 'chart' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSpendingViewMode('chart')}
                >
                  <PieChartIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {spending.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transactions yet</p>
            ) : spendingViewMode === 'chart' ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      onClick={(data) => navigate(`/transactions?category_id=${data.category_id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} style={{ cursor: 'pointer' }} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="space-y-4">
                {spending.slice(0, 5).map((cat, index) => {
                  const budgetStatus = getBudgetStatus(cat.total_amount, cat.budget_amount)
                  const hasBudget = cat.budget_amount && cat.budget_amount > 0

                  return (
                    <div
                      key={cat.category_id}
                      className="space-y-2 cursor-pointer hover:bg-accent/50 rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/transactions?category_id=${cat.category_id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{ backgroundColor: cat.category_color || DEFAULT_COLORS[index % DEFAULT_COLORS.length] }}
                          />
                          <span className="text-sm font-medium">{cat.category_name}</span>
                        </div>
                        <div className="text-right">
                          {hasBudget ? (
                            <div>
                              <span className={`text-sm font-medium ${budgetStatus.status === 'over' ? 'text-red-500' : ''}`}>
                                {formatCurrency(cat.total_amount)}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {' / '}{formatCurrency(cat.budget_amount!)}
                              </span>
                              {budgetStatus.status === 'over' && (
                                <p className="text-xs text-red-500">
                                  Over by {formatCurrency(cat.total_amount - cat.budget_amount!)}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm">{formatCurrency(cat.total_amount)}</span>
                          )}
                        </div>
                      </div>
                      <div className="relative">
                        <Progress
                          value={hasBudget
                            ? Math.min(budgetStatus.percentage, 100)
                            : (cat.total_amount / totalSpending) * 100
                          }
                          className={`h-2 ${getProgressColor(budgetStatus.status)}`}
                        />
                        {hasBudget && budgetStatus.status === 'over' && (
                          <div
                            className="absolute top-0 right-0 h-2 bg-red-500 rounded-r"
                            style={{
                              width: `${Math.min((budgetStatus.percentage - 100) / budgetStatus.percentage * 100, 30)}%`
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
